import { Component } from '@theme/component';
import { VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';
import { productSelectIdentity, productSelectedOptionIdentity } from './product-select-identity.js';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    const isSellingPlan =
      selectedOption instanceof HTMLInputElement && selectedOption.name === 'selling_plan';

    this.updateSelectedOption(event.target);
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const { standardDeferred, selectedOptions } = this.beginProductSelect(selectedOption);

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, standardDeferred, selectedOptions);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;
    const sellingPlanId = this.selectedSellingPlanId;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else if (!isSellingPlan) {
        url.searchParams.delete('variant');
      }

      if (sellingPlanId) {
        url.searchParams.set('selling_plan', sellingPlanId);
      } else {
        url.searchParams.delete('selling_plan');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Starts the public product-select boundary for both the normal picker path
   * and product-card alternate-available swatches.
   * @param {HTMLElement} selectedOption
   * @param {{variantId?: string, optionValueId?: string, connectedProductUrl?: string}} [overrides]
   */
  beginProductSelect(selectedOption, overrides = {}) {
    const standardDeferred = ProductSelectEvent.createPromise();
    standardDeferred.promise.catch(() => {});
    const selectedOptions = this.getAllSelectedOptions();
    const productCard = /** @type {HTMLElement | null} */ (this.closest('product-card'));

    this.dispatchEvent(
      new ProductSelectEvent({
        product: productSelectIdentity(this.dataset, productCard?.dataset),
        selectedOptions: selectedOptions.length
          ? selectedOptions
          : [productSelectedOptionIdentity(selectedOption, selectedOption.getAttribute('value'))],
        promise: standardDeferred.promise,
        detail: {
          optionValueId: overrides.optionValueId ?? selectedOption.dataset.optionValueId ?? '',
          variantId: overrides.variantId ?? selectedOption.dataset.variantId ?? '',
          connectedProductUrl: overrides.connectedProductUrl ?? selectedOption.dataset.connectedProductUrl ?? '',
        },
      })
    );

    return { standardDeferred, selectedOptions };
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const productUrlObject = productUrl ? new URL(productUrl, window.location.origin) : null;
    const viewParamValue = getViewParameterValue() || productUrlObject?.searchParams.get('view');

    if (productUrlObject) {
      productUrlObject.search = '';
      productUrl = productUrlObject.toString();
    }

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedSellingPlanId) {
      params.push(`selling_plan=${encodeURIComponent(this.selectedSellingPlanId)}`);
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  get selectedSellingPlanId() {
    const selectedSellingPlan = this.querySelector('input[name="selling_plan"]:checked');
    return selectedSellingPlan instanceof HTMLInputElement ? selectedSellingPlan.value : '';
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {ReturnType<typeof ProductSelectEvent.createPromise> | null} standardDeferred
   * @param {Array<{name: string, value: string}>} selectedOptions
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false, standardDeferred = null, selectedOptions = []) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          standardDeferred?.resolve({
            variant: null,
            detail: { html, productId: this.dataset.productId || '', sourceId: this.selectedOptionId, resource: null },
          });
          return;
        }

        const variantResource = JSON.parse(textContent);
        let newProduct;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const quickAddDialog = this.closest('dialog');

          if (this.classList.contains('ch-wotm-picker') && quickAddDialog) {
            const currentMediaGallery = quickAddDialog.querySelector('media-gallery');
            const updatedMediaGallery = html.querySelector('media-gallery');

            if (currentMediaGallery && updatedMediaGallery) {
              updatedMediaGallery.querySelectorAll('zoom-dialog').forEach((nestedDialog) => nestedDialog.remove());
              currentMediaGallery.replaceWith(updatedMediaGallery);
            }
          }

          newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch the rich private event.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantResource, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        standardDeferred?.resolve({
          variant: variantResource
            ? {
                id: String(variantResource.id || ''),
                title: String(variantResource.title || ''),
                availableForSale: Boolean(variantResource.available),
                price: {
                  amount: (Number(variantResource.price || 0) / 100).toFixed(2),
                  currencyCode:
                    this.dataset.currency ||
                    window.Shopify?.currency?.active ||
                    document.documentElement.dataset.currency ||
                    'USD',
                },
                selectedOptions:
                  selectedOptions.length > 0
                    ? selectedOptions
                    : [{ name: 'Option', value: String(variantResource.title || '') }],
              }
            : null,
          detail: {
            html,
            productId: this.dataset.productId || '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: variantResource,
          },
        });
      })
      .catch((error) => {
        standardDeferred?.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  getAllSelectedOptions() {
    const options = [];

    for (const select of /** @type {NodeListOf<HTMLSelectElement>} */ (this.querySelectorAll('select'))) {
      const selected = select.selectedOptions[0];
      if (selected) {
        options.push({
          ...productSelectedOptionIdentity(
            { dataset: { optionName: selected.dataset.optionName || select.dataset.optionName } },
            selected.value
          ),
        });
      }
    }

    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('fieldset input:checked'))) {
      options.push({
        ...productSelectedOptionIdentity(input, input.value),
      });
    }

    return options;
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return this.selectedSellingPlanId || undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
